<template>
    <el-dialog
        :visible="visible"
        :title="editing ? 'Sửa lịch cố định' : 'Thêm lịch cố định'"
        width="620px"
        append-to-body
        custom-class="tlfs-dialog"
        @update:visible="$emit('update:visible', $event)"
    >
        <el-form label-position="top" class="tlfs-form" @submit.native.prevent>
            <el-form-item v-if="showTeamLead" label="Teamlead áp dụng" :error="errors.user_id">
                <el-select
                    v-model="form.user_id"
                    filterable
                    clearable
                    placeholder="Chọn Teamlead"
                    class="w-100"
                    :disabled="editing"
                >
                    <el-option v-for="tl in teamLeads" :key="tl.id" :label="tl.name" :value="tl.id">
                        <span>{{ tl.name }}</span>
                        <span v-if="tl.team_name" class="tlfs-form__opt-sub">{{ tl.team_name }}</span>
                    </el-option>
                </el-select>
                <div v-if="editing" class="tlfs-form__hint">
                    Không đổi được Teamlead của lịch đã tạo — hãy xoá và tạo lịch mới nếu cần chuyển người.
                </div>
            </el-form-item>

            <el-form-item label="Nội dung công việc" :error="errors.task">
                <el-input
                    v-model="form.task"
                    maxlength="255"
                    show-word-limit
                    placeholder="Ví dụ: Họp phòng TCGV, Thăm lớp, Trực tuyển dụng..."
                />
            </el-form-item>

            <el-form-item label="Các thứ áp dụng trong tuần" :error="errors.date_of_week">
                <el-checkbox-group v-model="form.date_of_week" class="tlfs-form__days">
                    <el-checkbox-button v-for="d in weekdays" :key="d.value" :label="d.value">
                        {{ d.short }}
                    </el-checkbox-button>
                </el-checkbox-group>
                <div class="tlfs-form__quick">
                    <el-button type="text" size="mini" @click="pickDays([1, 2, 3, 4, 5])">T2–T6</el-button>
                    <el-button type="text" size="mini" @click="pickDays([1, 2, 3, 4, 5, 6])">T2–T7</el-button>
                    <el-button type="text" size="mini" @click="pickDays([6, 7])">Cuối tuần</el-button>
                    <el-button type="text" size="mini" @click="pickDays([])">Bỏ chọn</el-button>
                </div>
            </el-form-item>

            <div class="tlfs-form__row">
                <el-form-item label="Giờ bắt đầu" class="tlfs-form__col" :error="errors.time">
                    <el-time-select
                        v-model="form.start_time"
                        :picker-options="timeOptions"
                        placeholder="Bắt đầu"
                        class="w-100"
                    />
                </el-form-item>
                <el-form-item label="Giờ kết thúc" class="tlfs-form__col">
                    <el-time-select
                        v-model="form.end_time"
                        :picker-options="{ ...timeOptions, minTime: form.start_time }"
                        placeholder="Kết thúc"
                        class="w-100"
                    />
                </el-form-item>
                <el-form-item label="Áp dụng từ ngày" class="tlfs-form__col" :error="errors.start_date">
                    <el-date-picker
                        v-model="form.start_date"
                        type="date"
                        format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd"
                        placeholder="Chọn ngày"
                        :picker-options="{ firstDayOfWeek: 1 }"
                        class="w-100"
                    />
                </el-form-item>
            </div>

            <div class="tlfs-form__row">
                <el-form-item label="Áp dụng trong" class="tlfs-form__col">
                    <el-select v-model="form.duration" class="w-100" @change="onDurationChange">
                        <el-option v-for="d in durations" :key="d.value" :label="d.label" :value="d.value" />
                    </el-select>
                </el-form-item>
                <el-form-item label="Áp dụng đến ngày" class="tlfs-form__col" :error="errors.end_date">
                    <el-date-picker
                        v-model="form.end_date"
                        type="date"
                        format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd"
                        :placeholder="form.duration === unlimited ? 'Không giới hạn' : 'Chọn ngày'"
                        :disabled="form.duration === unlimited"
                        :picker-options="endDatePickerOptions"
                        class="w-100"
                        @change="form.duration = 'custom'"
                    />
                </el-form-item>
            </div>

            <div v-if="previewText" class="tlfs-form__preview">
                <i class="el-icon-magic-stick"></i> {{ previewText }}
            </div>
        </el-form>

        <div slot="footer">
            <el-button size="small" @click="$emit('update:visible', false)">Huỷ</el-button>
            <el-button size="small" type="primary" :loading="submitting" @click="submit">
                {{ editing ? "Lưu thay đổi" : "Tạo lịch cố định" }}
            </el-button>
        </div>
    </el-dialog>
</template>

<script>
import moment from "moment-timezone";
import {
    WEEKDAYS,
    WEEKDAY_LABEL,
    FIXED_SCHEDULE_DURATIONS,
    FIXED_SCHEDULE_UNLIMITED,
    fixedScheduleEndDate,
} from "@/core/config/teamLeadsCalendar/teamLeadsCalendarOption";

/** "08:00:00" | "08:00" -> "08:00" */
function toHm(value) {
    const m = value ? String(value).match(/^(\d{1,2}):(\d{2})/) : null;
    return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
}

function emptyForm() {
    return {
        id: null,
        user_id: null,
        task: "",
        date_of_week: [],
        start_time: "08:00",
        end_time: "10:00",
        start_date: moment().format("YYYY-MM-DD"),
        // Phạm vi áp dụng: mặc định lặp mãi như hành vi cũ
        duration: FIXED_SCHEDULE_UNLIMITED,
        end_date: null,
    };
}

/** Suy ngược preset phạm vi từ cặp ngày bắt đầu/kết thúc của bản ghi đã lưu */
function durationOf(startDate, endDate) {
    if (!endDate) return FIXED_SCHEDULE_UNLIMITED;
    const match = FIXED_SCHEDULE_DURATIONS.find(
        (d) => d.unit && fixedScheduleEndDate(startDate, d.value) === endDate
    );
    return match ? match.value : "custom";
}

export default {
    name: "FixedScheduleFormDialog",
    props: {
        visible: { type: Boolean, default: false },
        editing: { type: Boolean, default: false },
        /** Rule đã chuẩn hoá từ store (kèm `raw` là bản ghi gốc của API) */
        initial: { type: Object, default: () => ({}) },
        teamLeads: { type: Array, default: () => [] },
        showTeamLead: { type: Boolean, default: true },
        submitting: { type: Boolean, default: false },
        currentUserId: { type: [Number, String], default: null },
    },
    data() {
        return {
            form: emptyForm(),
            errors: {},
            weekdays: WEEKDAYS,
            durations: FIXED_SCHEDULE_DURATIONS,
            unlimited: FIXED_SCHEDULE_UNLIMITED,
            timeOptions: { start: "06:00", step: "00:15", end: "22:00" },
        };
    },
    computed: {
        /** Không cho chọn ngày kết thúc trước ngày bắt đầu áp dụng */
        endDatePickerOptions() {
            const start = this.form.start_date;
            return {
                firstDayOfWeek: 1,
                disabledDate: (date) => (start ? moment(date).format("YYYY-MM-DD") < start : false),
            };
        },
        /** Số tuần lịch thực sự chạy — giúp người tạo hình dung phạm vi đã chọn */
        weekCount() {
            if (!this.form.start_date || !this.form.end_date) return 0;
            const diff = moment(this.form.end_date).diff(moment(this.form.start_date), "days") + 1;
            return Math.max(1, Math.ceil(diff / 7));
        },
        previewText() {
            const days = [...this.form.date_of_week].sort((a, b) => a - b).map((v) => WEEKDAY_LABEL[v]);
            if (!days.length || !this.form.start_time) return "";
            const from = this.form.start_date ? moment(this.form.start_date).format("DD/MM/YYYY") : "";
            const time = `${this.form.start_time}${this.form.end_time ? `–${this.form.end_time}` : ""}`;
            const range = this.form.end_date
                ? ` · áp dụng ${from} – ${moment(this.form.end_date).format("DD/MM/YYYY")} (~${this.weekCount} tuần)`
                : `${from ? ` · áp dụng từ ${from}` : ""} · không giới hạn`;
            return `Lặp lại ${days.join(", ")} · ${time}${range}`;
        },
    },
    watch: {
        /** Đổi ngày bắt đầu thì tính lại ngày kết thúc theo preset đang chọn */
        "form.start_date"() {
            this.syncEndDate();
        },
    },
    watch: {
        visible(open) {
            if (open) this.reset();
        },
    },
    methods: {
        reset() {
            const base = emptyForm();
            const init = this.initial || {};
            // Ưu tiên bản ghi gốc của API, chỉ dùng field đã chuẩn hoá khi không có
            const raw = init.raw || {};

            base.id = init.id != null ? init.id : raw.id || null;
            base.user_id = raw.user_id != null ? raw.user_id : init.team_lead_id;
            if (base.user_id == null && !this.showTeamLead) base.user_id = this.currentUserId;
            base.task = raw.task || init.title || "";

            if (Array.isArray(raw.date_of_week) && raw.date_of_week.length) {
                base.date_of_week = raw.date_of_week.map(Number);
            } else if (Array.isArray(init.slots) && init.slots.length) {
                base.date_of_week = init.slots.map((s) => Number(s.weekday));
            }

            const slot = (init.slots || [])[0] || {};
            base.start_time = toHm(raw.start_time || slot.start_time) || base.start_time;
            base.end_time = toHm(raw.end_time || slot.end_time) || base.end_time;
            base.start_date = raw.start_date || init.effective_from || base.start_date;
            base.end_date = raw.end_date || init.effective_to || null;
            base.duration = durationOf(base.start_date, base.end_date);

            this.form = base;
            this.errors = {};
        },
        onDurationChange(value) {
            if (value === FIXED_SCHEDULE_UNLIMITED) {
                this.form.end_date = null;
                return;
            }
            this.syncEndDate();
        },
        /** Quy đổi preset phạm vi thành ngày kết thúc cụ thể ("custom" do người dùng tự chọn) */
        syncEndDate() {
            if (this.form.duration === FIXED_SCHEDULE_UNLIMITED || this.form.duration === "custom") return;
            this.form.end_date = fixedScheduleEndDate(this.form.start_date, this.form.duration);
        },
        pickDays(values) {
            this.form.date_of_week = [...values];
        },
        validate() {
            const e = {};
            const f = this.form;
            if (this.showTeamLead && !f.user_id) e.user_id = "Chọn Teamlead áp dụng lịch";
            if (!String(f.task || "").trim()) e.task = "Nhập nội dung công việc";
            if (!f.date_of_week.length) e.date_of_week = "Chọn ít nhất 1 thứ trong tuần";
            if (!f.start_date) e.start_date = "Chọn ngày bắt đầu áp dụng";
            if (!f.start_time) e.time = "Chọn giờ bắt đầu";
            else if (f.end_time && f.end_time <= f.start_time) e.time = "Giờ kết thúc phải sau giờ bắt đầu";
            if (f.duration !== FIXED_SCHEDULE_UNLIMITED && !f.end_date) e.end_date = "Chọn ngày kết thúc áp dụng";
            else if (f.end_date && f.end_date < f.start_date) e.end_date = "Ngày kết thúc phải sau ngày bắt đầu";
            this.errors = e;
            return !Object.keys(e).length;
        },
        submit() {
            if (!this.validate()) return;
            const f = this.form;
            this.$emit("submit", {
                editing: this.editing,
                id: f.id,
                payload: {
                    user_id: f.user_id,
                    task: String(f.task).trim(),
                    date_of_week: [...f.date_of_week].sort((a, b) => a - b),
                    start_time: `${f.start_time}:00`,
                    end_time: f.end_time ? `${f.end_time}:00` : null,
                    start_date: f.start_date,
                    // null = lặp không giới hạn
                    end_date: f.duration === FIXED_SCHEDULE_UNLIMITED ? null : f.end_date,
                },
            });
        },
    },
};
</script>

<style>
/* Ghi đè phần bên trong Element UI: dialog dùng custom-class="tlfs-dialog" */
.tlfs-dialog .el-dialog__body {
    padding: 10px 20px 0;
}
.tlfs-dialog .el-form-item {
    margin-bottom: 16px;
}
.tlfs-dialog .el-form-item__label {
    font-size: 12.5px;
    font-weight: 600;
    color: #55607a;
}
.tlfs-dialog .tlfs-form__days .el-checkbox-button__inner {
    padding: 7px 14px;
    border-radius: 8px !important;
    border-left: 1px solid #dcdfe6;
    margin-right: 6px;
}
.tlfs-dialog .tlfs-form__quick .el-button {
    padding: 2px 8px 2px 0;
    font-size: 11.5px;
}
</style>

<style scoped>
.tlfs-form__opt-sub {
    float: right;
    color: #a3a9b8;
    font-size: 12px;
}
.tlfs-form__hint {
    font-size: 11.5px;
    color: #99a0b0;
    line-height: 1.5;
    margin-top: 4px;
}
.tlfs-form__quick {
    margin-top: 4px;
}
.tlfs-form__row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}
.tlfs-form__col {
    flex: 1 1 160px;
    min-width: 150px;
}
.tlfs-form__preview {
    background: #f5f6fb;
    border-radius: 10px;
    padding: 9px 12px;
    font-size: 12.5px;
    color: #55607a;
    margin-bottom: 16px;
}
.tlfs-form__preview i {
    color: #6366f1;
}
</style>
